/**
 * generate 命令：读 JSON 中间层 → 校验 → 编译 XML。
 * 拆为两个语义清晰的子命令，避免「离线/部署」双模式共用一组开关导致的参数组合错误
 * （坑点#16/#19 同类：大输出冲 stdout、--publish 漏配 --deploy 被静默忽略）：
 *   - generate        : 纯离线编译，强制 --out 导出 XML，绝不写 stdout。
 *   - generate-deploy : 写入 BaskReport 服务端（默认仅保存；--publish 才发布），需显式凭证与 tenantId。
 */

import { readFileSync, writeFileSync } from 'node:fs';
import { createInterface } from 'node:readline/promises';
import { Command } from 'commander';
import { validateIR } from '../core/validate/schemaValidator.js';
import { structureCheck, collectExpressions } from '../core/validate/structureCheck.js';
import { verifyReportSql, verifyReportSqlWithSchema } from '../core/validate/sqlVerify.js';
import { resolveDatasource, resolveDatasetTables, resolveDatasetFields, resolveDatasetId, resolveBeanDatasetNames, type ConfirmFn } from '../core/validate/datasourceResolve.js';
import { serializeToXml } from '../core/xml/serialize.js';
import { parseReport } from '../core/xml/parse.js';
import { applyMasterDetailParents, extractMasterDetailAnnotation } from '../core/grid/regions.js';
import { requireAuth, resolveDeploy, applyConfigDir, isNonInteractive } from '../core/config.js';
import { withBudget, getBudgetCharger } from '../core/budget.js';
import { login, resolveTenantOrSelect, assertTenantId } from '../client/session.js';
import { checkExpressionSyntaxBatch } from '../client/datasourceApi.js';
import { createReport, deployReport, updateReportXml, publishReport, loadReportXml } from '../client/fileApi.js';
import { designerUrl, viewUrl } from '../client/urls.js';
import { getLogger } from '../core/logger.js';

const log = getLogger('cli:generate');

/** 给命令附加「编译期」共享选项（离线编译 / 服务端部署都生效的校验与解析开关）。 */
function applyCompileOptions(cmd: Command): Command {
  return cmd
    .requiredOption('--in <file>', '输入的中间层 JSON 文件')
    .option('--no-verify-sql', '跳过在线回源校验（SQL 与数据库表结构、BeanDataset 名称与服务端注册名单）（不推荐，仅在无服务端凭证时使用）')
    .option('--no-verify-expr', '跳过表达式语法在线校验（baskserver /designer/checkSyntax：本地 3f 启发式只能拦常见错误，函数名/参数个数/运算合法性的最终裁决在引擎端，error 非空即非法）（不推荐，仅在无服务端凭证时使用）')
    .option('--no-schema-ctx', 'SQL 校验失败时不再自动拉取真实表结构以给出「你是不是想写 X」的修正建议（默认开启）')
    .option('--strict-type', '类型不匹配升级为错误（默认仅警告）')
    .option('--require-grid', '强制要求 IR 含 rows/columns 行列定义（模板转换流程必带：rows[].height/columns[].width 从模板骨架原样搬入，缺失时报错而非静默用默认行高 26/列宽 80）')
    .option('--no-resolve-datasource', '关闭数据源动态解析：ds.id 缺失时直接报错，而不是通过 list/load 推断（默认开启解析）')
    .option('--no-resolve-tables', '关闭表存在性确认：SQL 引用的表在数据源下找不到时直接报错，而不是交互确认（默认开启确认）')
    .option('--yes', '非交互模式：遇到需要确认的数据源/表，不再询问，直接采用唯一候选或跳过确认')
    .option('--inherit-datasets <id>', '继承参考报表的数据集定义：自动拉取该 reportId 的数据集 SQL/fields 合并进本 IR（仅填充本 IR 未声明的项，同名数据集不覆盖已有 SQL/fields），并打 fromReportId 标记以跳过回源校验。省去反复 report datasets + 手抄 fields 的多轮交互。需服务端凭证。')
    .option('--summary', '成功路径仅输出一行校验结论（数据集数 / cells 数 / SQL·表达式校验结果），不打印详细过程日志，避免 LLM 读回完整 XML 或刷屏');
}

export function registerGenerate(program: Command) {
  // ---- 离线编译：强制 --out，绝不写 stdout ----
  applyCompileOptions(
    program
      .command('generate')
      .description('将报表中间层 JSON 编译为 BaskReport XML 并导出到本地文件（离线，不接触服务端）。')
      .requiredOption('--out <file>', '离线导出 XML 到文件（必填；避免完整 XML 冲入 stdout 占用上下文）'),
  ).action(withBudget(async (opts) => {
    try {
      applyConfigDir(opts);
      const { xml, summary } = await compileAndValidate(opts);
      writeFileSync(opts.out, xml, 'utf-8');
      getBudgetCharger().chargeText(xml); // 通道 B：落盘文本计入预算
      if (opts.summary) {
        // F：成功路径仅输出一行结论，避免 LLM 读回完整 XML 或刷屏。
        log.info(`generate 完成 → ${opts.out} | 数据集 ${summary.datasets} 个 / cells ${summary.cells} 个 | ${summary.sql} | ${summary.expr}`);
      } else {
        log.info(`已导出 XML：${opts.out}`);
      }
    } catch (e: any) {
      log.error(`generate 失败：${e.message}`, e);
      process.exitCode = 1;
    }
  }));

  // ---- 部署到服务端：默认仅保存；--publish 才发布 ----
  applyCompileOptions(
    program
      .command('generate-deploy')
      .description('将报表中间层 JSON 编译并写入 BaskReport 服务端（默认仅保存；--publish 才发布为正式资源）。')
      .option('--publish', '写入后再执行发布（deploy），使报表成为正式可用资源；省略则仅保存不发布')
      .option('--base-url <url>', '服务端地址，如 http://host:port')
      .option('--username <user>', '登录用户名（Session 登录；与 --access-token 互斥）')
      .option('--password <pwd>', '登录密码（Session 登录；与 --access-token 互斥）')
      .option('--access-token <token>', 'OAuth2 访问令牌（与 username/password 互斥；缺省读 BASK_ACCESS_TOKEN / .env）')
      .option('--access-token-header <name>', 'OAuth2 token 注入的 header 名，缺省 authorization（值形如 Bearer <token>）；缺省读 BASK_ACCESS_TOKEN_HEADER / .env')
      .option('--tenantId <id>', '租户 ID（必填；所有 baskserver 请求都必须携带租户标识，缺失即报错，不允许自动解析到错误租户）')
      .option('--id <id>', '报表文件 ID（提供则覆盖更新该 id 的已有报表；省略则新建报表）')
      .option('--name <name>', '报表名称（新建时可选，缺省取 JSON.name）')
      .option('--parent-id <id>', '父目录 ID，缺省 0（根）'),
  ).action(withBudget(async (opts) => {
    try {
      applyConfigDir(opts);
      const { xml, summary } = await compileAndValidate(opts);

      // 部署阶段必须显式提供凭证与 tenantId（不允许省略后自动解析，避免落到错误租户，坑点#8）。
      const deployCfg = resolveDeploy(opts);
      requireAuth(deployCfg, 'generate-deploy');
      const session = await login(deployCfg);
      session.tenantId = assertTenantId(session); // 与 authedRequest / datasource 同款守卫

      // 报表定位统一由 id 确定：有 --id 则覆盖更新已有报表，无 --id 则新建（code 留空）。
      const file = opts.id != null
        ? { id: Number(opts.id) }
        : await createReport(session, {
            parentId: opts.parentId ? Number(opts.parentId) : 0,
            name: deployCfg.name ?? opts.name ?? 'untitled-report',
          });
      // 修改既有报表前，先发布当前线上版本：让后台封存一个可回滚快照，
      // 后期新内容出问题可恢复到修改前。仅覆盖更新（提供 --id）时才有意义；
      // 新建报表无可快照版本，publishReport 返回 false 自动跳过。
      if (opts.id != null) {
        try {
          const prePublished = await publishReport(session, file.id);
          if (prePublished) log.info('已发布修改前的当前版本（用于后续可恢复）');
        } catch (e) {
          log.warn(`  [跳过] 修改前发布当前版本失败：${e instanceof Error ? e.message : e}（将继续更新）`);
        }
      }
      // 落地顺序（已按服务端实际契约确认）：create → update → deploy。
      await updateReportXml(session, file.id, xml);
      let published = false;
      if (opts.publish) {
        try {
          await deployReport(session, file.id);
          published = true;
        } catch (e) {
          log.warn(`  deploy 提示：${e instanceof Error ? e.message : e}（报表已保存但未发布成功）`);
        }
      }
      log.info(`报表已写入服务端${published ? '并已发布' : '（仅保存，未发布）'}：`);
      if (opts.summary) {
        // F：成功路径仅追加一行结论，URL 仍给出（定位用，体积很小）。
        log.info(`  校验结论：数据集 ${summary.datasets} 个 / cells ${summary.cells} 个 | ${summary.sql} | ${summary.expr}`);
      }
      log.info(`  设计器: ${designerUrl(session.baseUrl, file.id, session.tenantId)}`);
      log.info(`  预览:   ${viewUrl(session.baseUrl, file.id, session.tenantId)}`);
    } catch (e: any) {
      log.error(`generate-deploy 失败：${e.message}`, e);
      process.exitCode = 1;
    }
  }));
}

/**
 * 读 IR → 结构自检 → 在线回源校验（SQL/表达式，按需登录）→ 序列化 XML。
 * 离线编译与部署共用此流程。在线校验需登录时，允许「自动解析唯一租户」（校验阶段
 * 容忍省略 tenantId；但部署阶段的 tenantId 由调用方用 assertTenantId 强制显式）。
 * 返回 { xml, datasetIdLookup, ir }。
 */
async function compileAndValidate(opts: any): Promise<{ xml: string; datasetIdLookup: Map<string, string>; ir: any; summary: { datasets: number; cells: number; sql: string; expr: string } }> {
  const raw = readFileSync(opts.in, 'utf-8');
  let sqlErrCount = 0; // F：累计 SQL 校验 error 数，供 --summary 结论输出。
  // 方案 Y：先剥离旁路字段 masterDetailAnnotation（不写入 schema，validateIR 前移除），
  // 交由 applyMasterDetailParents 在序列化前补全 left。
  const { report: parsedReport, annotation } = extractMasterDetailAnnotation(JSON.parse(raw));
  const ir = validateIR(parsedReport);

  // 登录态按需复用：inherit-datasets / SQL/Bean 校验 / 表达式校验 都可能要登录，
  // 统一通过 ensureSession 惰性登录一次，避免重复登录。
  let session: import('../client/session.js').Session | undefined;
  let authCfg: ReturnType<typeof resolveDeploy> | undefined;
  const ensureSession = async (): Promise<import('../client/session.js').Session> => {
    if (session) return session;
    authCfg = resolveDeploy(opts);
    requireAuth(
      authCfg,
      '在线操作需服务端凭证（--inherit-datasets 继承参考数据集 / 在线回源校验 / 表达式校验）；' +
        '确无凭证时可用 --no-verify-sql --no-verify-expr 跳过校验，但 --inherit-datasets 仍需凭证。',
    );
    session = await login(authCfg);
    session.tenantId = await resolveTenantOrSelect(session, authCfg.tenantId);
    return session;
  };

  // G：继承参考报表数据集（仅填充本 IR 未声明的项，同名不覆盖已有 SQL/fields，并打 fromReportId）。
  // 放在 structureCheck 之前：填充的 SQL/fields 需参与后续结构自检与在线校验；
  // 带 fromReportId 的数据集在 resolveReportDatasources 中跳过回源（L407 同款信任标记）。
  if (opts.inheritDatasets) {
    const s = await ensureSession();
    await inheritDatasets(ir, String(opts.inheritDatasets), s);
  }

  // 模板类 IR（source:'template'）自动强制 requireGrid 校验：无需 AI 显式传 --require-grid，
  // 只要 IR 自声明来自模板转换，即强制检查 rows/columns 行列定义，从源头杜绝版式失真。
  const requireGrid = !!(opts.requireGrid) || ir.source === 'template';
  const issues = structureCheck(ir, { requireGrid });
  const errors = issues.filter((i) => i.level === 'error');
  if (errors.length) {
    log.error('结构自检发现错误：');
    for (const e of errors) log.error(`  - ${e.cell ?? ''} ${e.message}`.trim());
    throw new Error('结构自检未通过，已中止。');
  }
  for (const w of issues.filter((i) => i.level === 'warn')) {
    log.warn(`  [warn] ${w.cell ?? ''} ${w.message}`.trim());
  }

        // 在线回源校验（默认开启）：LLM 写出的 SQL 只是「猜测」，必须与数据库真实表结构比对，
        // 否则表名/列名拼错、类型不符等问题要到预览报表时才暴露；BeanDataset 名称同样必须与
        // 服务端注册名单核对（自起别名会查不到数据），这些是离线结构自检覆盖不到的。
        // 无论 --deploy 还是 --out 离线导出都会校验；仅 --no-verify-sql 可显式跳过。
        const hasSqlDataset = (ir.datasets ?? []).some((d) => d.type === 'sql' && d.sql);
        const hasBeanDataset = (ir.datasets ?? []).some((d) => d.type === 'bean');
        // 表达式语法交由后端 checkSyntax 权威校验：本地 3f 启发式只能拦常见错误，
        // 函数名/参数个数/运算合法性的最终裁决在 baskserver 设计器引擎（error 非空即非法）。
        // 同一表达式多处引用只校验一次（错误定位首个出处），避免重复请求。
        const exprItems = collectExpressions(ir);
        const seenExpr = new Set<string>();
        const exprToCheck = exprItems.filter((it) => {
          if (seenExpr.has(it.expr)) return false;
          seenExpr.add(it.expr);
          return true;
        });
        const sqlBeanOnline = (hasSqlDataset || hasBeanDataset) && opts.verifySql !== false;
        const needExprCheck = exprToCheck.length > 0 && opts.verifyExpr !== false;
        const needsOnlineValidation = sqlBeanOnline || needExprCheck;
        let resolveResult: { ok: boolean; datasetIdLookup: Map<string, string> } = {
          ok: true,
          datasetIdLookup: new Map(),
        };
        if ((hasSqlDataset || hasBeanDataset) && opts.verifySql === false) {
          log.warn(
            '  [warn] 已通过 --no-verify-sql 跳过在线回源校验：SQL 表结构 / BeanDataset 名称未经服务端确认，\n' +
              '         表名/列名拼写错误或 BeanDataset 别名只会在预览报表时才暴露。建议先用 datasource schema / list-bean-datasets 探查。',
          );
        }
        let sessionForExpr: import('../client/session.js').Session | undefined = session;
        if (sqlBeanOnline) {
          session = session ?? (await ensureSession());

          // 数据源与表的动态解析：数据源不必在 IR 里写死（如一律假设 samplecenter-jdbc 是 MySQL），
          // 而是先用列表/load-datasource-defs 推断真实 id/type，再对每条 SQL 引用的表用
          // build-database-tables 确认其确实存在；无法确认（表名拼错、多个候选）时与用户确认。
          // 其中对 type=bean 的数据集额外做名称回源校验（见 resolveBeanDatasetNames）。
          resolveResult = await resolveReportDatasources(session, ir, {
            tenantId: session.tenantId,
            skipDatasourceResolve: opts.resolveDatasource === false,
            skipTableResolve: opts.resolveTables === false,
            confirm: makeConfirm(opts.yes === true || isNonInteractive()),
          });
          if (!resolveResult.ok) {
            throw new Error('数据源/表在线解析未通过，已中止。');
          }

          // 默认走 verifyReportSqlWithSchema：自动按报表引用的数据源拉取真实表结构，
          // 校验失败时给出「你是不是想写 X」的表名/字段建议，而非只抛「构建字段失败」，
          // 让 LLM/用户能据此自动修正。--no-schema-ctx 可退回纯校验（不额外拉 schema）。
          if (hasSqlDataset) {
            const sqlIssues = opts.schemaCtx === false
              ? await verifyReportSql(session, ir, { strictType: !!opts.strictType })
              : await verifyReportSqlWithSchema(session, ir, { strictType: !!opts.strictType });
            const sqlErr = sqlIssues.filter((i) => i.level === 'error');
            sqlErrCount = sqlErr.length;
            // E：压缩 SQL 校验输出——按数据集分组、对「你是不是想写 X」建议去重、单数据集限行数，
            // 避免多数据集 × 多条建议刷屏灌入 LLM 上下文（stderr 不计入预算但占上下文）。
            const MAX_PER_DS = 5;
            const byDs = new Map<string, typeof sqlIssues>();
            for (const s of sqlIssues) {
              const key = s.dataset ?? '(未知数据集)';
              let bucket = byDs.get(key);
              if (!bucket) {
                bucket = [];
                byDs.set(key, bucket);
              }
              bucket.push(s);
            }
            for (const [ds, items] of byDs) {
              const seenSuggest = new Set<string>();
              let printed = 0;
              for (const s of items) {
                if (printed >= MAX_PER_DS) break;
                const tag = s.level === 'error' ? 'ERROR' : 'warn';
                // 「你是不是想写 X」类建议去重：相同候选只提示一次。
                const m = /你是不是想写[：:]\s*(.+)$/.exec(s.message ?? '');
                if (m && m[1]) {
                  const cand = m[1].trim();
                  if (seenSuggest.has(cand)) continue;
                  seenSuggest.add(cand);
                }
                log.error(`  [sql:${tag}] (${ds}) ${s.message}`);
                printed++;
              }
              if (items.length > MAX_PER_DS) {
                log.error(`  [sql] (${ds}) ... 另有 ${items.length - MAX_PER_DS} 条，已省略（修正上方后重试即可暴露剩余项）`);
              }
            }
            if (sqlErr.length) {
              log.error(
                'SQL 校验未通过：数据集 SQL 与数据库表结构不匹配，请依据上方「建议/你是不是想写」提示修正后重试。\n' +
                  '前置预防：写 SQL 前先运行 `datasource schema-ctx --datasource-id <id>`，把真实表结构粘进 prompt，避免猜表名/列名。',
              );
              throw new Error(
                'SQL 校验未通过：数据集 SQL 与数据库表结构不匹配，请依据上方「建议/你是不是想写」提示修正后重试。' +
                  '前置预防：写 SQL 前先运行 `datasource schema-ctx --datasource-id <id>`，把真实表结构粘进 prompt，避免猜表名/列名。',
              );
            }
            log.info('SQL 校验通过：所有 SQL 数据集均与数据库表结构一致。');
          }
        }

        // 表达式语法在线校验：批量调用 baskserver /designer/checkSyntaxBatch（一次请求携带全部去重后的
        // 表达式，服务端逐条裁决并回填 {index,id,ok,error}，error 非空即非法）。本地 3f 只是第一道闸，
        // 函数名/参数个数/运算合法性的最终裁决在引擎。服务端单批上限 500 条，超出自动分批。
        if (needExprCheck) {
          session = session ?? (await ensureSession());
          // id 回传单元格坐标（where），服务端原样回填 results[].id，便于精确报错定位；
          // 剥离 "=" 前缀（服务端也会自动剥离，这里提前剥离便于空串过滤）。
          const exprItemsToCheck = exprToCheck
            .map((it) => ({ id: it.where, expr: it.expr.replace(/^\s*=\s*/, '').trim() }))
            .filter((it) => it.expr.length > 0);
          const exprErrors: string[] = [];
          const MAX_BATCH = 500; // 服务端单批上限，超出分批
          for (let start = 0; start < exprItemsToCheck.length; start += MAX_BATCH) {
            const chunk = exprItemsToCheck.slice(start, start + MAX_BATCH);
            let res;
            try {
              res = await checkExpressionSyntaxBatch(session, chunk);
            } catch (e) {
              log.warn(
                `  [expr:warn] 批量表达式语法校验请求失败：${e instanceof Error ? e.message : String(e)}（跳过本批 ${chunk.length} 项）`,
              );
              continue;
            }
            if (res?.error) {
              log.warn(`  [expr:warn] 批量校验被服务端拒绝：${res.error}（跳过本批 ${chunk.length} 项）`);
              continue;
            }
            const byId = new Map((res?.results ?? []).map((r) => [String(r.id), r]));
            for (const it of chunk) {
              const r = byId.get(it.id);
              const err = r?.error;
              if (err) {
                exprErrors.push(it.id);
                log.error(`  [expr:ERROR] ${it.id} (=${it.expr})：${String(err)}`);
              } else {
                log.info(`  [expr:ok] ${it.id} (=${it.expr})`);
              }
            }
          }
          if (exprErrors.length) {
            log.error(
              '表达式语法校验未通过：请按上方引擎报错逐条修正。生成表达式前先运行 `datasource functions` 拉取真实函数库；' +
                '字段值先绑定到单元格（content.kind:"dataset"）再以单元格引用判断（如 =if(B2=="男","男","女")），' +
                '不要直接写字段名（=sex?"男":"女" 非法）。',
            );
            throw new Error(
              '表达式语法校验未通过：请按上方引擎报错逐条修正。生成表达式前先运行 `datasource functions` 拉取真实函数库；' +
                '字段值先绑定到单元格（content.kind:"dataset"）再以单元格引用判断（如 =if(B2=="男","男","女")），' +
                '不要直接写字段名（=sex?"男":"女" 非法）。',
            );
          }
          log.info(`表达式语法校验通过：${exprItemsToCheck.length} 个表达式均通过 checkSyntaxBatch。`);
        }

        // 序列化前应用主从区域标注补全 left（若 AI 提供了 masterDetailAnnotation）。
        let finalIr = ir;
        if (annotation) {
          const applied = applyMasterDetailParents(ir, annotation);
          finalIr = { ...ir, cells: applied.cells };
          for (const w of applied.warnings) {
            log.warn(`  [warn] ${w.cell ?? ''} ${w.message}`.trim());
          }
        }

        const xml = serializeToXml(finalIr, resolveResult.datasetIdLookup);
        // F：汇总校验结论，供 --summary 成功路径精简输出（避免 LLM 读回完整 XML）。
        const sqlState = !hasSqlDataset
          ? '无SQL数据集'
          : opts.verifySql === false
            ? 'SQL校验已跳过(--no-verify-sql)'
            : sqlErrCount === 0
              ? 'SQL校验通过'
              : 'SQL校验未通过';
        const exprState = exprToCheck.length === 0
          ? '无表达式'
          : opts.verifyExpr === false
            ? '表达式校验已跳过(--no-verify-expr)'
            : '表达式校验通过';
        const summary = {
          datasets: (finalIr.datasets ?? []).length,
          cells: (finalIr.cells ?? []).length,
          sql: sqlState,
          expr: exprState,
        };
        return { xml, datasetIdLookup: resolveResult.datasetIdLookup, ir, summary };
}

/**
 * G：继承参考报表的数据集定义，合并进本 IR。
 * 语义「仅填充未声明项」：本 IR 已有同名 dataset 时，不覆盖其已有 SQL/fields，
 * 仅补充缺失的 SQL（本身无 sql 才补）与缺失的 fields（按 name 补齐本 IR 没声明的字段）；
 * 本 IR 没有但参考报表有的数据集按同名规则整体补入。
 * 所有被填充/补入的数据集一律打 fromReportId 标记（原样取自参考报表，符合「SQL/fields
 * 一字未改 + 同一数据源」的信任条件），使后续 resolveReportDatasources 跳过回源校验。
 * 注意：若 AI 后续改写某数据集的 SQL 或字段，须自删 fromReportId（SKILL 红线已规范）。
 */
async function inheritDatasets(ir: any, refId: string, session: import('../client/session.js').Session): Promise<void> {
  const xml = await loadReportXml(session, Number(refId));
  const refIr = parseReport(xml);
  if (!refIr) {
    throw new Error(`参考报表 #${refId} 根节点不是 <report>（可能是仪表盘/图表），无法继承其数据集。`);
  }
  const refByName = new Map((refIr.datasets ?? []).map((d: any) => [d.name, d]));
  if (refByName.size === 0) {
    log.warn(`  [inherit] 参考报表 #${refId} 未包含任何数据集，未做继承。`);
    return;
  }

  const existing = ir.datasets ?? [];
  const merged = existing.map((d: any) => {
    const ref = refByName.get(d.name);
    if (!ref) return d;
    const mergedFields = mergeFields(d.fields ?? [], ref.fields ?? []);
    const wasFilled = !d.sql || mergedFields.length > (d.fields?.length ?? 0);
    return {
      ...d,
      sql: d.sql ?? ref.sql,
      fields: mergedFields,
      fromReportId: Number(refId),
      ...(wasFilled ? {} : {}), // 占位，下面统一日志
    };
  });

  // 补充本 IR 缺失、但参考报表有的数据集（整体补入，原样带 fromReportId）。
  const haveNames = new Set(existing.map((d: any) => d.name));
  for (const ref of refIr.datasets ?? []) {
    if (!haveNames.has(ref.name)) {
      merged.push({ ...ref, fromReportId: Number(refId) });
    }
  }

  ir.datasets = merged;
  const filled = merged.filter((d: any) => d.fromReportId === Number(refId));
  log.info(
    `已继承参考报表 #${refId} 的数据集：${filled.length}/${merged.length} 个带 fromReportId（SQL/fields 原样复用，跳过回源校验）。` +
      `同名数据集仅补缺失项，未覆盖你显式声明的 SQL/fields。`,
  );
}

/** 合并字段：以 base（本 IR 已声明）为主，仅追加 ref 中 base 按 name 不存在的字段（不覆盖、不重排）。 */
function mergeFields(base: any[], ref: any[]): any[] {
  const byName = new Map(base.map((f) => [f.name, f]));
  const out = [...base];
  for (const f of ref) {
    if (!byName.has(f.name)) out.push(f);
  }
  return out;
}

/**
 * 解析整个报表引用的数据源，并确认每条 SQL 数据集引用的表/对象确实存在；
 * 同时对 type=bean 的数据集做名称回源校验（与服务端注册名单比对，别名直接阻断）。
 * 直接修改 ir（补全 datasources 的 id/type/kind、规范化 bean 名称及单元格引用，使其能被 serialize 正确引用）。
 * 返回 { ok, datasetIdLookup }：ok=false 表示遇到无法确认且用户放弃的情况，调用方应中止；
 * datasetIdLookup 为「数据源名\u0000数据集名 → 服务端 datasetId」组合键映射，供 serialize 输出
 * API 数据集的 dataset-id（datasetId 不再持久化在 IR 中，改为按 数据源id+数据集名 从服务器反查）。
 * 用组合键而非单纯数据集名，是因为不同数据源下可能同名数据集，单以 name 为键会互相覆盖。
 */
async function resolveReportDatasources(
  session: import('../client/session.js').Session,
  ir: import('../core/ir/types.js').ReportIR,
  opts: {
    tenantId?: string;
    skipDatasourceResolve?: boolean;
    skipTableResolve?: boolean;
    confirm: ConfirmFn<any>;
  },
): Promise<{ ok: boolean; datasetIdLookup: Map<string, string> }> {
  const datasetIdLookup = new Map<string, string>();

  // BeanDataset 名称回源校验：type=bean 的数据集没有 SQL，离线结构自检覆盖不到名称是否
  // 服务端真实注册名（自起别名会在运行时查不到数据），必须在连服务端时用注册名单核对。
  // 命中（大小写不同）→ 自动规范化回写并同步单元格引用；未命中（疑似别名）→ 阻断并给候选。
  const beanResolve = await resolveBeanDatasetNames(session, ir, { tenantId: opts.tenantId });
  for (const r of beanResolve.renamed) {
    console.log(`BeanDataset "${r.from}" 已按服务端注册名称规范化为 "${r.to}"`);
  }
  if (!beanResolve.ok) {
    console.error(`BeanDataset 名称未在服务端注册名单中：${beanResolve.unknown.join('、')}。`);
    console.error(
      'BeanDataset 的 datasets[].name 必须与 list-bean-datasets 返回的原始名称完全一致（禁止自起别名），' +
        '否则报表运行时查不到数据。',
    );
    return { ok: false, datasetIdLookup };
  }

  const datasources = ir.datasources ?? [];
  if (datasources.length === 0) return { ok: true, datasetIdLookup };

  const resolved = new Map<string, string>(); // 原 name/别名 → 真实 datasource id
  // 数据源 name → 解析后的 DatasourceIR（含 datasourceType），供数据集反查 datasetId 使用。
  const resolvedDsByName = new Map<string, import('../core/ir/types.js').DatasourceIR>();

  for (const ds of datasources) {
    let resolvedDs: import('../core/ir/types.js').DatasourceIR;
    try {
      resolvedDs = await resolveDatasource(session, ds, {
        tenantId: opts.tenantId,
        skipDatasourceResolve: opts.skipDatasourceResolve,
        confirm: opts.confirm,
      });
    } catch (e) {
      console.error(`数据源 "${ds.name}" 解析失败：${e instanceof Error ? e.message : e}`);
      return { ok: false, datasetIdLookup };
    }
    if (resolvedDs.id) {
      resolved.set(ds.name, String(resolvedDs.id));
    }
    ds.id = resolvedDs.id;
    ds.kind = resolvedDs.kind;
    ds.type = resolvedDs.type;
    ds.datasourceType = resolvedDs.datasourceType;
    resolvedDsByName.set(ds.name, resolvedDs);
    if (resolvedDs.id && resolvedDs.id !== ds.id) {
      console.log(`数据源 "${ds.name}" 已解析为服务端数据源：${resolvedDs.id}（type=${resolvedDs.type ?? '未知'}）`);
    }
  }

  // 逐数据集确认 SQL 引用的表/对象；同时对 API 数据集反查 datasetId（派生信息，不进 IR）。
  for (const ds of ir.datasets ?? []) {
    const dsId = resolved.get(ds.datasource);
    const refDs = resolvedDsByName.get(ds.datasource);

    // API / Adapt / Virtual 服务端数据集：按 数据源id + 数据集名 反查 datasetId，
    // 装入 datasetIdLookup 供 serialize 输出 XML 的 dataset-id。
    // 判断依据为关联数据源的 datasourceType（api/adapt/virtual），而非数据集自身 type。
    if (dsId && (refDs?.datasourceType === 'api' || refDs?.datasourceType === 'adapt' || refDs?.datasourceType === 'virtual')) {
      try {
        const did = await resolveDatasetId(session, dsId, ds.name, refDs.datasourceType, {
          tenantId: opts.tenantId,
          skipTableResolve: opts.skipTableResolve,
          confirm: opts.confirm,
        });
        // 组合键：数据源名 + NUL + 数据集名，避免不同数据源下同名数据集相互覆盖。
        datasetIdLookup.set(`${refDs.name}\u0000${ds.name}`, did);
      } catch (e) {
        console.error(`数据集 "${ds.name}" 反查 datasetId 失败：${e instanceof Error ? e.message : e}`);
        return { ok: false, datasetIdLookup };
      }
    }

    if (!dsId || ds.type !== 'sql' || !ds.sql) continue;
    // 声明式信任标记：数据集原样取自参考报表（fromReportId），其 SQL/fields 已由线上报表验证，
    // 跳过 buildDatabaseTables / buildFields 回源，避免重复加载。前提约束见 DatasetIR.fromReportId 注释：
    // 仅当 SQL 与 fields 一字未改、且关联数据源为同一服务端数据源（同一库）时才允许保留该标记。
    if (ds.fromReportId) {
      console.log(`数据集 "${ds.name}" 声明来自参考报表 #${ds.fromReportId}（原样复用），跳过表/字段回源校验。`);
      continue;
    }
    try {
      const { unknown } = await resolveDatasetTables(session, dsId, ds, {
        tenantId: opts.tenantId,
        skipTableResolve: opts.skipTableResolve,
        confirm: opts.confirm,
      });
      if (unknown.length) {
        console.error(
          `数据集 "${ds.name}" 引用的表在数据源 ${dsId} 下无法确认：${unknown.join(', ')}。` +
          `请核对表名，或先用 \`datasource build-database-tables --datasource-id ${dsId}\` 查看可用表。`,
        );
        return { ok: false, datasetIdLookup };
      }

      // 字段回源校验：用 buildFields 拿真实列，比对 ds.fields 与单元格引用，
      // 防止 LLM 臆造列名（structureCheck 只能校验 field∈ds.fields，而 ds.fields 本身可能臆造）。
      const fchk = await resolveDatasetFields(session, dsId, ds, ir, {
        tenantId: opts.tenantId,
        skipTableResolve: opts.skipTableResolve,
        confirm: opts.confirm,
      });
      if (!fchk.ok) {
        console.error(`数据集 "${ds.name}" 字段与数据库真实结构不符：`);
        if (fchk.irFieldsMissingInDb.length) {
          console.error(`  - 声明的字段在库中不存在（疑似臆造）：${fchk.irFieldsMissingInDb.join(', ')}`);
        }
        if (fchk.unknownCellRefs.length) {
          console.error(`  - 单元格引用的字段在库中不存在：${fchk.unknownCellRefs.join(', ')}`);
        }
        return { ok: false, datasetIdLookup };
      }
    } catch (e) {
      console.error(`数据集 "${ds.name}" 表确认失败：${e instanceof Error ? e.message : e}`);
      return { ok: false, datasetIdLookup };
    }
  }

  return { ok: true, datasetIdLookup };
}

/** 构造交互确认回调；非交互模式（--yes）直接采用唯一候选，否则用 readline 询问。 */
function makeConfirm(nonInteractive: boolean): ConfirmFn<any> {
  return async (prompt: string, candidates: any[]) => {
    if (candidates.length === 0) return null;
    if (nonInteractive) {
      // 仅当唯一候选时自动采纳；多个候选在 --yes 下不臆测，直接放弃并交由后续校验兜底。
      return candidates.length === 1 ? candidates[0] : null;
    }
    const rl = createInterface({ input: process.stdin, output: process.stderr });
    try {
      console.error(`\n${prompt}`);
      candidates.forEach((c, i) => console.error(`  ${i + 1}. ${formatCandidate(c)}`));
      const ans = (await rl.question(`请选择编号（回车跳过/取消）：`)).trim();
      if (!ans) return null;
      const idx = Number(ans) - 1;
      return candidates[idx] ?? null;
    } finally {
      rl.close();
    }
  };
}

function formatCandidate(c: any): string {
  if (typeof c === 'string') return c;
  return c.name ? `${c.name}${c.type ? ` (${c.type})` : ''}${c.id && c.id !== c.name ? ` id=${c.id}` : ''}` : JSON.stringify(c);
}
